import React from 'react';

import { Link } from 'react-router-dom';

import {

  Dialog,

  DialogContent,

  DialogDescription,

  DialogHeader,

  DialogTitle,

} from '../ui/dialog';

import { Button } from '../ui/button';

import { BookOpen, ClipboardCheck, Download, FileJson, Flame, Loader2, Sparkles } from 'lucide-react';

import { AiLegacyBookExportPanel } from '../export/AiLegacyBookExportPanel';
import { TrustProofStrip } from '../trust/TrustProofGrid';
import { ContextualGuideTip } from '../guide/ContextualGuideTip';
import morrisonArchivePreview from '../../assets/landing/morrison-archive-preview.jpg';
import { memorialsHref } from '../../utils/memorial';



/**

 * Preview & export modal — legacy book PDF, AI-assisted draft workflow, memorial links.

 */

export function ArchivePreviewModal({

  open,

  onOpenChange,

  treeId,

  treeName,

  readinessHref,

  canExport,

  peopleCount = 0,

  onExportPdf,

  onExportJson,

  exportingPdf,

  exportingJson,

}) {

  return (

    <Dialog open={open} onOpenChange={onOpenChange}>

      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-[hsl(38_16%_86%)] bg-[hsl(42_28%_99%)] shadow-xl sm:max-w-xl">

        <DialogHeader className="space-y-1 text-left">

          <div className="flex items-center gap-2 text-[hsl(32_18%_36%)]">

            <Sparkles className="h-5 w-5 shrink-0" aria-hidden />

            <DialogTitle className="font-serif text-xl text-[hsl(24_14%_16%)]">

              Preview your legacy book

            </DialogTitle>

          </div>

          <DialogDescription className="text-[hsl(32_10%_40%)]">

            {treeName ? (

              <>

                Shape <span className="font-medium text-foreground/90">{treeName}</span> into a keepsake legacy book.
                Prepare with AI, review before export, then download your keepsake PDF.

              </>

            ) : (

              <>Prepare with AI, review before export, and download your family keepsake.</>

            )}

          </DialogDescription>

        </DialogHeader>



        <ContextualGuideTip tipId="preview-export" className="mt-2">
          Create a Legacy Book PDF or draft review copy. Audio originals stay in your archive.
        </ContextualGuideTip>

        <div className="mt-4 space-y-5">

          {treeId ? (

            <AiLegacyBookExportPanel

              treeId={treeId}

              canExport={canExport}

              peopleCount={peopleCount}

              onExportPdf={onExportPdf}

              exportingPdf={exportingPdf}

              compact

            />

          ) : null}



          <div
            className="overflow-hidden rounded-2xl border border-[hsl(38_14%_82%)] bg-gradient-to-b from-[hsl(42_30%_100%)] to-[hsl(38_22%_94%)] shadow-inner"
            data-testid="archive-preview-mockup"
          >
            <div className="relative aspect-[16/10] overflow-hidden border-b border-[hsl(38_14%_82%)]">
              <img
                src={morrisonArchivePreview}
                alt=""
                className="absolute inset-0 h-full w-full object-cover object-[center_35%] opacity-90 saturate-[0.88] sepia-[0.06]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1c1917]/50 via-[#fffcf7]/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                <p className="font-serif text-sm text-[#faf7f2]">Legacy Book preview</p>
                <p className="mt-0.5 text-[10px] uppercase tracking-wider text-[#faf7f2]/75">Stories · profiles · documents</p>
              </div>
            </div>
            <div className="px-5 py-4 text-center">
              <p className="font-serif text-sm font-medium text-[hsl(24_12%_22%)]">Turn preserved memories into a Legacy Book</p>
              <p className="mt-2 text-xs leading-relaxed text-[hsl(32_10%_44%)]">
                Stories, profiles, and documents you&apos;ve completed — gathered into one keepsake PDF.
              </p>
            </div>
          </div>



          {readinessHref ? (

            <Button type="button" variant="secondary" className="w-full justify-center rounded-full" asChild>

              <Link to={readinessHref} onClick={() => onOpenChange?.(false)} data-testid="archive-preview-readiness-link">

                <ClipboardCheck className="mr-2 h-4 w-4" aria-hidden />

                View keepsake readiness

              </Link>

            </Button>

          ) : null}



          <div className="rounded-xl border border-border/40 bg-muted/10 p-4 space-y-2">

            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Memorial &amp; storybook</p>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">

              {treeId ? (

                <Button type="button" variant="outline" size="sm" className="rounded-full" asChild>

                  <Link to={memorialsHref(treeId)} onClick={() => onOpenChange?.(false)}>

                    <Flame className="mr-1.5 h-3.5 w-3.5" />

                    Memorials

                  </Link>

                </Button>

              ) : null}

            </div>

          </div>



          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">

            {canExport && onExportPdf && (

              <Button type="button" className="rounded-full" disabled={exportingPdf} onClick={() => onExportPdf()}>

                {exportingPdf ? (

                  <>

                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />

                    Preparing PDF…

                  </>

                ) : (

                  <>

                    <Download className="mr-2 h-4 w-4" />

                    Download keepsake PDF

                  </>

                )}

              </Button>

            )}

            {canExport && onExportJson && (

              <Button

                type="button"

                variant="outline"

                className="rounded-full border-[hsl(38_14%_82%)]"

                disabled={exportingJson}

                onClick={() => onExportJson()}

              >

                {exportingJson ? (

                  <>

                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />

                    Preparing backup…

                  </>

                ) : (

                  <>

                    <FileJson className="mr-2 h-4 w-4" />

                    Download backup (JSON)

                  </>

                )}

              </Button>

            )}

            {(!canExport || (!onExportPdf && !onExportJson)) && (

              <p className="text-sm text-muted-foreground">

                Add a few people and memories first — full export lives in Archive settings → Exports.

              </p>

            )}

          </div>

          <TrustProofStrip className="border-t border-border/30 pt-4" />
        </div>

      </DialogContent>

    </Dialog>

  );

}


